import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Role,
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { updateCancelAtEndOfPeriod } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { toUUID } from "helpers/s12-helpers";
import { upCaseString } from "helpers/utils";
import {
  UpdateCancelAtEndOfPeriodProps,
  UrlParamTypes,
} from "types/org-display";

const UpdateCancelAtEndOfPeriod = ({
  refreshOrgData,
}: UpdateCancelAtEndOfPeriodProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const subscription = orgData?.subscription?.[0];
  const isZuoraOrg = subscription?.billingProvider === "ZUORA";
  const zuoraSubscription = subData?.subscription;

  const currentCancelAtEndOfPeriod = isZuoraOrg
    ? zuoraSubscription?.cancelAtEndOfPeriod
    : subscription?.chargify?.cancelAtEndOfPeriod;

  const [targetCancelAtEndOfPeriod, setTargetCancelAtEndOfPeriod] = useState(
    !currentCancelAtEndOfPeriod
  );

  const isSubStateValid = isZuoraOrg
    ? zuoraSubscription?.state !== undefined &&
      ![SubscriptionState.CANCELED, SubscriptionState.PAST_DUE].includes(
        zuoraSubscription.state
      )
    : !subscription?.chargify?.state &&
      [ChargifyState.CANCELED, ChargifyState.PAST_DUE].includes(
        subscription?.chargify?.state || ChargifyState.UNSPECIFIED
      );

  const isButtonDisabled =
    isSubscriptionActionLoading ||
    [SubscriptionType.FREE, SubscriptionType.FREE_ORG].includes(
      subscription?.type
    ) ||
    (!subscription?.active && !isSubStateValid);

  const handleCancelAtEndOfPeriodState = (oldSubscription: Subscription) => {
    if (isZuoraOrg) {
      return;
    }
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      if (oldSubscription?.chargify?.cancelAtEndOfPeriod) {
        newOrgData.subscription[0].chargify.cancelAtEndOfPeriod =
          oldSubscription.chargify.cancelAtEndOfPeriod;
      }
      setOrgData(newOrgData);
    }
  };

  const cancelAtEndOfPeriodWithMessage = () => {
    setCancelAtEndOfPeriod(cancelMessage);
  };

  const handleClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const setCancelAtEndOfPeriod = (message?: string) => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    updateCancelAtEndOfPeriod(
      {
        organizationId: toUUID(roleId),
        subscriptionId: subscription.id,
        cancelAtEndOfPeriod: targetCancelAtEndOfPeriod,
        message,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        refreshOrgData();
        setOpenCancelDialog(false);
        setOpenSuccessAlert(true);
        handleCancelAtEndOfPeriodState(subscription);
        setTargetCancelAtEndOfPeriod(!targetCancelAtEndOfPeriod);
        setCancelMessage("");
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const isCancelAtPeriodEndMsg = () => {
    if (!targetCancelAtEndOfPeriod) {
      return (
        <React.Fragment>
          <Typography sx={classes.spaceTop}>
            Subscription cancelling at end of billing period.
          </Typography>
        </React.Fragment>
      );
    }
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title="Cancel at end of period:"
        displayInfo={
          currentCancelAtEndOfPeriod !== undefined
            ? upCaseString(currentCancelAtEndOfPeriod.toString())
            : upCaseString(false.toString())
        }
        middleElem={isCancelAtPeriodEndMsg()}
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="btn-update-cancel-end-of-period"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isButtonDisabled}
            onClick={() => {
              if (targetCancelAtEndOfPeriod) {
                setOpenCancelDialog(true);
              } else {
                setCancelAtEndOfPeriod();
              }
            }}
          >
            {targetCancelAtEndOfPeriod
              ? "Cancel at end of period"
              : "Clear pending cancellation"}
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Cancel at end of period"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={openCancelDialog}
        setOpen={setOpenCancelDialog}
        onConfirm={() => {
          cancelAtEndOfPeriodWithMessage();
        }}
        onCancel={() => {
          setOpenCancelDialog(false);
        }}
      >
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          paddingTop={"20px"}
        >
          <Grid item xs={2}>
            <TextField
              inputProps={
                {
                  "data-testid": "cancel-end-of-period-reason-text-field",
                } as any // eslint-disable-line
              }
              fullWidth
              label="Reason for cancellation (optional)"
              multiline
              rows={5}
              variant={"outlined"}
              onChange={(e) => setCancelMessage(e.target.value)}
              value={cancelMessage}
            />
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
        data-testid="snackbar-update-cancel-at-end-of-period"
      >
        <Alert severity="success" variant="filled">
          {targetCancelAtEndOfPeriod ? "Initiated" : "Reverted"} cancel at end
          of period
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default UpdateCancelAtEndOfPeriod;
